import React, { type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, IconButton, Paper, Stack, Typography, useTheme } from '@mui/material';
import { AxiosError } from 'axios';
import _isEqual from 'lodash/isEqual';

import {
  FolderType,
  LocationDTO,
  TalentDTO,
  UpdateTalentLocations500,
  useGetTalentById,
  useUpdateTalentLocations
} from '@/api/generated';
import CheckIcon from '@/assets/icons/check.svg?react';
import WarningIcon from '@/assets/icons/warning-red.svg?react';
import XSmallIcon from '@/assets/icons/x-small.svg?react';
import StandardPopper from '@/components/StandardPopper/StandardPopper';
import { OrganizationStructureProvider, useOrganizationStructure } from '@/hooks/useOrganizationStructure';

import LocationTreeSelect from './LocationTreeSelect';

interface TalentLocationTreeSelectFieldProps {
  talentId: string;
  organizationSlug: string;
  disabled?: boolean;
  allowedTypes?: FolderType[];
}

const TalentLocationTreeSelectField: FC<TalentLocationTreeSelectFieldProps> = ({
  talentId,
  organizationSlug,
  disabled,
  allowedTypes
}) => {
  const { t } = useTranslation(['talents', 'common']);
  const { spacing, palette } = useTheme();
  const { data, refetch } = useGetTalentById(talentId!, { query: { enabled: !!talentId } });
  const { mutateAsync: updateTalentLocations, isLoading: isUpdating } = useUpdateTalentLocations();

  const talent = data as TalentDTO;

  const value = useMemo(() => talent?.locations?.map(location => location.slug!) ?? [], [talent]);

  const [inputValue, setInputValue] = useState(value);
  const [error, setError] = useState<string>('');

  const hasChanges = useMemo(() => {
    const convertToObject = (val: string[] | undefined) => {
      if (Array.isArray(val)) {
        return val.reduce((acc, item) => ({ ...acc, [item]: true }), {});
      }

      return val;
    };

    return !_isEqual(convertToObject(inputValue), convertToObject(value));
  }, [inputValue, value]);

  const { mapStructure } = useOrganizationStructure();

  const handleSave = useCallback(async () => {
    try {
      const response = await updateTalentLocations({
        talentId: talentId!,
        organizationSlug: organizationSlug!,
        data: {
          locations: inputValue?.map(location => ({ slug: location, type: mapStructure[location]?.type }))
        }
      });
      await refetch();

      setInputValue(response.locations?.map((location: LocationDTO) => location.slug!) ?? []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      if (error instanceof AxiosError) {
        const errorString = (error as AxiosError<UpdateTalentLocations500>).response?.data.error as string | undefined;
        setError(errorString ?? t('layouts.talentEditModal.locationUpdateFailed'));
      } else {
        setError(t('layouts.talentEditModal.locationUpdateFailed'));
      }
    }
  }, [updateTalentLocations, talentId, organizationSlug, inputValue, refetch, mapStructure, t]);

  const handleCancel = useCallback(() => {
    setInputValue(value);
    setError('');
  }, [value]);

  const handleChange = useCallback((newValue: string[] | string) => {
    setError('');
    setInputValue(newValue as string[]);
  }, []);

  return (
    <Box className="TalentLocationTreeSelect-root">
      <Stack direction="row" alignItems="center" gap={1} position="relative">
        <LocationTreeSelect
          organizationSlug={organizationSlug}
          value={inputValue}
          onChange={handleChange}
          isMulti
          disabled={disabled}
          allowedTypes={allowedTypes}
        />
        <Box position="absolute" right={0} top={0} width="auto" height="100%">
          <Stack direction="row" position="absolute" left={10} top={0} alignItems="center" gap={1} height="100%">
            {hasChanges && !error && !isUpdating && (
              <>
                <IconButton onClick={handleSave} sx={{ '& .CheckIcon-root': { color: 'grey.600' } }}>
                  <CheckIcon className="CheckIcon-root" />
                </IconButton>
                <IconButton onClick={handleCancel} sx={{ '& .XSmallIcon-root': { color: 'grey.600' } }}>
                  <XSmallIcon className="XSmallIcon-root" />
                </IconButton>
              </>
            )}
            {isUpdating && <CircularProgress size={24} />}
            {error && !isUpdating && (
              <StandardPopper
                placement="bottom"
                caretEndPosition="50%"
                getSx={() => ({ zIndex: 10000 })}
                treeRootSelector=".MuiDialog-container"
                content={
                  <Paper
                    sx={{
                      mt: 1,
                      p: spacing(0.75, 1.5),
                      position: 'relative',
                      maxWidth: '300px',
                      '.StandardPopper-root &': {
                        bgcolor: palette.primary[100]
                      }
                    }}
                  >
                    <Typography variant="body2" color="text.primary">
                      {error}
                    </Typography>
                  </Paper>
                }
              >
                <Box sx={{ '& .WarningIcon-root': { width: 24, height: 24 } }}>
                  <WarningIcon className="WarningIcon-root" />
                </Box>
              </StandardPopper>
            )}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

const TalentLocationTreeSelect: FC<TalentLocationTreeSelectFieldProps> = ({ talentId, organizationSlug, ...props }) => (
  <OrganizationStructureProvider organizationSlug={organizationSlug}>
    <TalentLocationTreeSelectField talentId={talentId} organizationSlug={organizationSlug} {...props} />
  </OrganizationStructureProvider>
);

export default TalentLocationTreeSelect;
